import {
  ResetPasswordPage,
  type ResetPasswordPageMessages,
  useConfirmPasswordResetMutation,
  useSendPasswordResetCodeMutation,
} from "@assetwatch/auth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const resetPasswordSearchSchema = z.object({
  code: z.coerce.string().optional(),
  email: z.string().optional(),
  expired: z.union([z.enum(["true", "false"]), z.boolean()]).optional(),
});

type ResetPasswordSearch = z.infer<typeof resetPasswordSearchSchema>;

export const Route = createFileRoute("/login/reset")({
  validateSearch: (search): ResetPasswordSearch =>
    resetPasswordSearchSchema.parse(search),
  component: ResetPasswordRoute,
});
function isExpiredSearchValue(expired: ResetPasswordSearch["expired"]) {
  return expired === true || expired === "true";
}
function ResetPasswordRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const confirmPasswordResetMutation = useConfirmPasswordResetMutation();
  const sendPasswordResetCodeMutation = useSendPasswordResetCodeMutation();
  const messages: ResetPasswordPageMessages = {
    assetWatchLabel: "AssetWatch",
    backToSignIn: t("auth.reset.backToSignIn"),
    confirmNewPasswordLabel: t("auth.reset.confirmNewPasswordLabel"),
    continueToSignIn: t("auth.reset.continueToSignIn"),
    description: t("auth.reset.description"),
    emailLabel: t("auth.reset.emailLabel"),
    emailPlaceholder: t("auth.reset.emailPlaceholder"),
    emailSentMessage: t("auth.reset.emailSentMessage"),
    emailSentTitle: t("auth.reset.emailSentTitle"),
    expiredLinkDescription: t("auth.reset.expiredLinkDescription"),
    expiredLinkTitle: t("auth.reset.expiredLinkTitle"),
    forgotPasswordDescription: t("auth.reset.description"),
    forgotPasswordTitle: t("auth.reset.forgotPasswordTitle"),
    hidePassword: t("auth.reset.hidePassword"),
    loadingSendingCode: t("auth.reset.loadingSendingCode"),
    loadingUpdatingPassword: t("auth.reset.loadingUpdatingPassword"),
    newPasswordLabel: t("auth.reset.newPasswordLabel"),
    passwordRequirementsIntro: t("auth.reset.passwordRequirementsIntro"),
    passwordSavedMessage: t("auth.reset.passwordSavedMessage"),
    passwordSavedTitle: t("auth.reset.passwordSavedTitle"),
    passwordsDoNotMatch: t("auth.reset.passwordsDoNotMatch"),
    reachOutToSupport: t("auth.reset.reachOutToSupport"),
    resetCodeLabel: t("auth.reset.resetCodeLabel"),
    saveChanges: t("auth.reset.saveChanges"),
    sendNewLink: t("auth.reset.sendNewLink"),
    sendResetCode: t("auth.reset.sendResetCode"),
    showPassword: t("auth.reset.showPassword"),
    supportQuestion: t("auth.reset.supportQuestion"),
    title: t("auth.reset.title"),
    errors: {
      codeMismatch: t("auth.reset.errors.codeMismatch"),
      expiredCode: t("auth.reset.errors.expiredCode"),
      generic: t("auth.reset.errors.generic"),
      invalidPassword: t("auth.reset.errors.invalidPassword"),
      limitExceeded: t("auth.reset.errors.limitExceeded"),
      passwordHistoryViolation: t("auth.reset.errors.passwordHistoryViolation"),
      unverifiedContact: t("auth.reset.errors.unverifiedContact"),
      userNotFound: t("auth.reset.errors.userNotFound"),
    },
  };
  return (
    <ResetPasswordPage
      confirmPasswordReset={(variables) =>
        confirmPasswordResetMutation.mutateAsync(variables)
      }
      initialCode={search.code}
      initialEmail={search.email}
      initialExpired={isExpiredSearchValue(search.expired)}
      isConfirming={confirmPasswordResetMutation.isPending}
      isSendingCode={sendPasswordResetCodeMutation.isPending}
      messages={messages}
      onNavigateToSignIn={() => void navigate({ to: "/" })}
      sendPasswordResetCode={(username) =>
        sendPasswordResetCodeMutation.mutateAsync({ username })
      }
    />
  );
}
